import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

const KEY = 'va.cart.v1';

interface CartState {
  ids: string[];
  coupon: string;
  has(id: string): boolean;
  add(id: string): void;
  remove(id: string): void;
  clear(): void;
  setCoupon(code: string): void;
}

const CartContext = createContext<CartState | null>(null);

function load(): { ids: string[]; coupon: string } {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw);
      return { ids: Array.isArray(v.ids) ? v.ids.filter((x: unknown) => typeof x === 'string') : [], coupon: typeof v.coupon === 'string' ? v.coupon : '' };
    }
  } catch {
    /* storage unavailable */
  }
  return { ids: [], coupon: '' };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [state]);

  // Keep tabs in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => e.key === KEY && setState(load());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<CartState>(
    () => ({
      ids: state.ids,
      coupon: state.coupon,
      has: (id) => state.ids.includes(id),
      add: (id) => setState((s) => (s.ids.includes(id) ? s : { ...s, ids: [...s.ids, id] })),
      remove: (id) => setState((s) => ({ ...s, ids: s.ids.filter((x) => x !== id) })),
      clear: () => setState({ ids: [], coupon: '' }),
      setCoupon: (coupon) => setState((s) => ({ ...s, coupon })),
    }),
    [state],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart outside CartProvider');
  return ctx;
}
